import type { Agent, AgentRole, Entity, Persisted } from '@tunasong/schemas'
import { isAgent } from '@tunasong/schemas'
import { entitiesApi } from '@tunasong/redux'
import type { TunaThunkDispatch } from '@tunasong/redux'
import type { AgentDefinition, Command } from './plugin-types.js'

const roleForParent = (parentType: string): AgentRole => {
  switch (parentType) {
    case 'song':
      return 'song-agent'
    case 'folder':
      return 'folder-agent'
    default:
      return 'generic'
  }
}

interface AgentCommandContext {
  dispatch: TunaThunkDispatch
  getState: () => unknown
  navigateToEntity: (entity: Persisted<Entity>) => void
}

/**
 * Convert an AgentDefinition into commands for the entity command menu.
 * - When no agent with this definitionId exists: "Add Agent: X" (creates and navigates)
 * - When one already exists: "Open Agent: X" (navigates to it)
 */
export const agentDefinitionToCommands = (
  definition: AgentDefinition,
  entity: Persisted<Entity>,
  context: AgentCommandContext
): Command[] => {
  const { dispatch, getState, navigateToEntity } = context

  // Check cache synchronously for existing agent
  const cached = entitiesApi.endpoints.loadChildEntities.select({ parentId: entity.id })(getState() as never)
  const children = cached?.data ?? []
  const existing = children.filter(isAgent) as Persisted<Agent>[]
  const existingAgent = existing.find(a => a.definitionId === definition.id)

  if (existingAgent) {
    return [
      {
        id: `agent-open-${definition.id}`,
        name: `Open Agent: ${definition.name}`,
        Icon: definition.Icon,
        blockOnRun: false,
        cmd: () => {
          navigateToEntity(existingAgent)
        },
      },
    ]
  }

  return [
    {
      id: `agent-add-${definition.id}`,
      name: `Add Agent: ${definition.name}`,
      Icon: definition.Icon,
      blockOnRun: true,
      cmd: async () => {
        const config = {
          proactivity: (definition.defaultConfig?.proactivity ?? 'normal') as Agent['config']['proactivity'],
          triggers: definition.defaultConfig?.triggers ?? { onChange: true },
        }

        const agentEntity: Partial<Agent> = {
          type: 'agent',
          parentId: entity.id,
          name: definition.name,
          definitionId: definition.id,
          roleLine: definition.roleLine,
          role: roleForParent(entity.type),
          status: 'active',
          config,
          memory: {},
        }

        const result = await dispatch(
          entitiesApi.endpoints.createEntity.initiate({
            entity: agentEntity as Agent,
            parent: entity,
          }) as never
        )

        const created = (result as { data?: Persisted<Entity> }).data
        if (created) {
          navigateToEntity(created)
        }
      },
    },
  ]
}
